import { App } from '@capacitor/app';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { useRef, useState, useEffect, useCallback } from 'react';

import { isBluetoothEnabled } from 'src/hooks/use-device-permissions';

import { starPrintService } from 'src/services/printers';
import { getPlatformPrinter } from 'src/platform/services';

// ----------------------------------------------------------------------

export type BluetoothAvailability =
  | 'checking' // initial probe in flight
  | 'on' // authorized + radio powered on
  | 'radioOff' // authorized but the radio is off → guide to Control Center
  | 'permissionOff' // denied/restricted → guide to Settings
  | 'unknown'; // notDetermined/unsupported → don't block, don't prompt

export interface UseBluetoothAvailabilityResult {
  state: BluetoothAvailability;
  /** Re-run the point-in-time check (e.g. a manual "Re-check" button). */
  recheck: () => void;
}

/**
 * Reactive Bluetooth availability for the printer Troubleshoot view.
 *
 * Reads the SILENT authorization first (never instantiates a CBCentralManager,
 * so no iOS permission prompt) and only probes the radio once already
 * authorized. This means opening Troubleshoot can't surprise-prompt a user who
 * has never granted Bluetooth:
 *   - authorized + radio on   → 'on'
 *   - authorized + radio off  → 'radioOff'      (Control Center guidance)
 *   - denied / restricted     → 'permissionOff' (Settings guidance)
 *   - notDetermined / unknown → 'unknown'        (don't block, don't prompt)
 *
 * When authorized it subscribes to live radio changes — toggling Bluetooth in
 * Control Center doesn't background the app, so `appStateChange` won't fire —
 * and also re-checks on app resume to catch a permission change made in Settings.
 *
 * The gate is `bluetoothPeripherals`, a capability, and not
 * `Capacitor.isNativePlatform()` (SITE_TRIAGE bucket B1). Electron is "native"
 * by that predicate (AMENDMENT A3.2) but a desktop register has no Bluetooth
 * peripherals path at all, so it would have sat on 'checking' forever waiting
 * for an authorization answer that was never coming.
 */
export function useBluetoothAvailability(): UseBluetoothAvailabilityResult {
  const [state, setState] = useState<BluetoothAvailability>('checking');
  // Tracks whether the radio listener is registered, so cleanup is exact.
  const subscribedRef = useRef(false);

  const check = useCallback(async (): Promise<BluetoothAvailability> => {
    let next: BluetoothAvailability;
    try {
      if (!getPlatformPrinter().bluetoothPeripherals) {
        next = 'on'; // Web and desktop: no Bluetooth peripherals to gate on.
      } else {
        const { status } = await starPrintService.getBluetoothAuthorization();
        if (status === 'denied' || status === 'restricted') {
          next = 'permissionOff';
        } else if (status !== 'authorized') {
          // notDetermined/unknown — probing the radio would prompt; stay silent.
          next = 'unknown';
        } else {
          next = (await isBluetoothEnabled()) ? 'on' : 'radioOff';
        }
      }
    } catch (error) {
      // Fail open — never block troubleshooting on a detection error.
      console.warn('[useBluetoothAvailability] check failed:', error);
      next = 'unknown';
    }
    setState(next);
    return next;
  }, []);

  const recheck = useCallback(() => {
    setState('checking');
    check();
  }, [check]);

  useEffect(() => {
    let cancelled = false;
    let removeAppListener: (() => void) | null = null;

    const run = async () => {
      const first = await check();
      // Only authorized states ('on'/'radioOff') reach here; subscribing to live
      // radio updates is then prompt-free. Skipped where there is no radio.
      if (cancelled || !getPlatformPrinter().bluetoothPeripherals) return;
      if (first !== 'on' && first !== 'radioOff') return;
      try {
        await BleClient.startEnabledNotifications((enabled) => {
          if (!cancelled) setState(enabled ? 'on' : 'radioOff');
        });
        subscribedRef.current = true;
      } catch (error) {
        console.warn('[useBluetoothAvailability] enabled-notifications failed:', error);
      }
    };
    run();

    // Catch a permission change made in Settings (app leaves + resumes).
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && !cancelled) check();
    })
      .then((handle) => {
        if (cancelled) handle.remove();
        else removeAppListener = () => handle.remove();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (removeAppListener) removeAppListener();
      if (subscribedRef.current) {
        subscribedRef.current = false;
        BleClient.stopEnabledNotifications().catch(() => {});
      }
    };
  }, [check]);

  return { state, recheck };
}
